import React, { useEffect, useRef } from 'react';

// Fireworks animation overlay that fills its parent container
// Renders a canvas absolutely positioned within the container
export default function FireworksOverlay() {
  const containerRef = useRef(null);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    let rafId = null;
    let running = true;

    // Position canvas to fill container
    Object.assign(canvas.style, {
      position: 'absolute',
      top: '0',
      left: '0',
      width: '100%',
      height: '100%',
      pointerEvents: 'none',
      zIndex: '0'
    });

    container.appendChild(canvas);

    const DPR = Math.min(window.devicePixelRatio || 1, 2);
    let width = container.clientWidth;
    let height = container.clientHeight;

    function resize() {
      width = container.clientWidth;
      height = container.clientHeight;
      canvas.width = Math.max(1, Math.floor(width * DPR));
      canvas.height = Math.max(1, Math.floor(height * DPR));
    }

    resize();

    const ro = new ResizeObserver(resize);
    ro.observe(container);

    const particles = [];
    const rockets = [];

    function rand(min, max) { return Math.random() * (max - min) + min; }
    function hsvToRgb(h, s, v) {
      let r, g, b;
      let i = Math.floor(h * 6);
      let f = h * 6 - i;
      let p = v * (1 - s);
      let q = v * (1 - f * s);
      let t = v * (1 - (1 - f) * s);
      switch (i % 6) {
        case 0: r = v; g = t; b = p; break;
        case 1: r = q; g = v; b = p; break;
        case 2: r = p; g = v; b = t; break;
        case 3: r = p; g = q; b = v; break;
        case 4: r = t; g = p; b = v; break;
        case 5: r = v; g = p; b = q; break;
        default: r = v; g = t; b = p;
      }
      return `rgb(${Math.round(r*255)}, ${Math.round(g*255)}, ${Math.round(b*255)})`;
    }

    function spawnRocket() {
      const x = rand(width * 0.15, width * 0.85);
      const targetY = rand(height * 0.25, height * 0.6);
      rockets.push({ x, y: height + 10, vx: rand(-0.4, 0.4), vy: rand(-8, -11), targetY, exploded: false, color: hsvToRgb(Math.random(), 0.8, 1) });
    }

    function explode(rocket) {
      const count = Math.floor(rand(35, 70));
      for (let i = 0; i < count; i++) {
        const angle = rand(0, Math.PI * 2);
        const speed = rand(1.8, 4.8);
        const vx = Math.cos(angle) * speed;
        const vy = Math.sin(angle) * speed;
        particles.push({
          x: rocket.x,
          y: rocket.y,
          vx,
          vy,
          life: rand(45, 90),
          alpha: 1,
          decay: rand(0.012, 0.024),
          color: rocket.color,
          size: rand(1, 2)
        });
      }
    }

    let lastSpawn = 0;

    function step() {
      if (!running) return;
      rafId = requestAnimationFrame(step);
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.save();
      ctx.scale(DPR, DPR);

      const now = performance.now();
      if (now - lastSpawn > 950) {
        spawnRocket();
        lastSpawn = now;
      }

      // Update rockets
      for (let i = rockets.length - 1; i >= 0; i--) {
        const r = rockets[i];
        r.x += r.vx;
        r.y += r.vy;
        r.vy += 0.10; // gravity
        if (!r.exploded && r.y <= r.targetY) {
          r.exploded = true;
          explode(r);
          rockets.splice(i, 1);
        } else {
          // draw rocket
          ctx.beginPath();
          ctx.arc(r.x, r.y, 2, 0, Math.PI * 2);
          ctx.fillStyle = r.color;
          ctx.fill();
        }
      }

      // Update particles
      for (let i = particles.length - 1; i >= 0; i--) {
        const p = particles[i];
        p.x += p.vx;
        p.y += p.vy;
        p.vy += 0.05; // gravity
        p.alpha -= p.decay;
        if (p.alpha <= 0 || --p.life <= 0) {
          particles.splice(i, 1);
          continue;
        }
        ctx.globalAlpha = Math.max(p.alpha, 0);
        ctx.fillStyle = p.color;
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
        ctx.fill();
        ctx.globalAlpha = 1;
      }

      ctx.restore();
    }

    step();

    return () => {
      running = false;
      if (rafId) cancelAnimationFrame(rafId);
      ro.disconnect();
      try { container.removeChild(canvas); } catch {}
    };
  }, []);

  return (
    <div ref={containerRef} style={{ position: 'absolute', inset: 0, pointerEvents: 'none', zIndex: 0 }} />
  );
}
